import * as React from 'react';

import { VariantDisplayType } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { giftLabel } from '@components/Tags/model';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { getGiftCampaigns } from '../Gift';
import { TagDiscount } from '../VariantsInSelectBox/TagDiscount';

import { GiftLabel } from './GiftLabel';
import { PriceDisplay } from './PriceDisplay';
import { Container, FlexWrapper, Name, Line, LineWrapper } from './styled';
import { usePriceLabelState } from './usePriceLabelState';

export interface ISelectedVariantProps {
  displayType: VariantDisplayType;
  singleVariant?: boolean;
  renderTagDiscount: boolean;
  variantName: React.ReactNode;

  hideLine?: boolean;
  hideVariantName?: boolean;
}

export const SelectedVariant: React.FC<ISelectedVariantProps> = React.memo(
  ({
    renderTagDiscount,
    variantName,
    hideLine = false,
    hideVariantName = false,
  }) => {
    const priceState = usePriceLabelState();
    const { product, currentVariant } = useProductDetailContext();
    const isSmall = priceState === 'default';
    const { bazaarvoice_pd_components } = useFeatureFlags();
    const { hasGiftCampaigns } = getGiftCampaigns({
      product,
      variant: currentVariant,
    });

    const shouldShowGiftLabel = hasGiftCampaigns && !renderTagDiscount;

    return (
      <ErrorBoundary>
        <Container
          id="pdSelectedVariant"
          bazaarvoice_pd_components={bazaarvoice_pd_components}
        >
          {renderTagDiscount && <TagDiscount variant={currentVariant} />}

          {shouldShowGiftLabel && <GiftLabel icon={giftLabel.icon} />}

          <FlexWrapper aria-live="assertive" tabIndex={0}>
            <Name isSmall={isSmall}>
              {hideVariantName ? null : variantName}
            </Name>

            <PriceDisplay variant={currentVariant} />
          </FlexWrapper>
          {!hideLine && (
            <LineWrapper>
              <Line />
            </LineWrapper>
          )}
        </Container>
      </ErrorBoundary>
    );
  }
);

SelectedVariant.displayName = 'SelectedVariant';
